<template>
  <div class="bg-white">
    <div class="bg-white py-4">
      <div class="table-max m-auto mt-4 px-6 md:pr-0">
        <div class="flex items-center justify-between pr-2">
          <div class="flex flex-col items-start">
            <h1 class="text-left text-3xl text-dark-blue">
              {{ title }}
            </h1>
          </div>

          <div
            v-if="isDesktop"
            class="flex items-center gap-2"
          >
            <LoadSortMenu :is-disabled="doesNotMeetRequirements"></LoadSortMenu>

            <ButtonComponent
              :disabled="doesNotMeetRequirements"
              @click="handleRefreshView"
            >
              <img
                src="@/assets/refresh-icon.svg"
                alt="fresh icon"
              />
              Refresh
            </ButtonComponent>

            <ButtonComponent
              :disabled="disabledPostCapacity"
              variant="primary"
              @click="openPostCapacityModal"
            >
              Post Capacity
            </ButtonComponent>
          </div>
        </div>

        <LoadsSearchAndFilter
          v-if="!isDesktop"
          @search-loads="searchLoadsInput"
        ></LoadsSearchAndFilter>
      </div>
    </div>

    <div class="table-max m-auto">
      <div
        v-if="!isDesktop"
        class="max-w-screen ml-6 mt-4 flex justify-between gap-3 overflow-scroll sm:mx-6"
      ></div>

      <div
        v-if="!isDesktop"
        class="flex items-center justify-between px-6"
      >
        <div
          v-if="!doesNotMeetRequirements"
          class="w-full"
        >
          <p v-if="isLoading"></p>

          <div
            v-else
            class="flex justify-between gap-3"
          >
            <p class="text-left">
              {{ numberOfLoadsOnLoadBoard }}
            </p>

            <div v-if="loads?.length > 0">
              <ButtonComponent
                variant="subtle"
                class="min-w-max"
                @click="openSortBottomSheet"
              >
                <img
                  src="@/assets/arrows-up-and-down.svg"
                  alt="arrows icon"
                />
                Sort: {{ postingSort }}
              </ButtonComponent>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isDesktop">
        <div class="flex flex-row pb-4 pr-2">
          <LoadFiltersDesktop
            :is-loads-dashboard="isLoadsDashboard"
            :is-resetting-search="isResettingSearch"
            :is-refresh-search="isRefreshSearch"
          ></LoadFiltersDesktop>

          <div
            v-if="isLoading"
            class="flex w-full items-center justify-center rounded-md border border-steel-300 bg-white shadow-md"
            :class="{
              'h-[calc(100vh-166px)]': !bannerError,
              'h-[calc(100vh-194px)]': bannerError,
            }"
          >
            <div>
              <img
                class="loading m-auto h-12 w-12"
                src="@/assets/loading-v2-yellow.svg"
                alt="loading spinner"
              />
              <p class="mt-2 text-base">Loading</p>
            </div>
          </div>

          <div
            v-else-if="doesNotMeetRequirements"
            :class="{
              'm-auto': isMissingOriginState,
              'mx-auto': !isMissingOriginState,
            }"
          >
            <div
              v-if="isMissingOriginState"
              class="m-auto flex flex-col items-center gap-3"
            >
              <img
                src="@/assets/loads/semi-truck.svg"
                alt="semi truck"
              />

              <h3
                class="text-left text-xl font-semibold text-neutral-subtle-bold"
              >
                Let's get you on the road
              </h3>
              <p class="text-center font-normal text-neutral-subtle-bold">
                To get started, enter an origin, destination, or broker.
              </p>
            </div>
            <div
              v-else
              class="m-auto mt-16 flex h-min w-[500px] flex-col gap-3 rounded-lg border border-steel-300 bg-white p-4 shadow-md"
            >
              <h3
                class="text-left text-xl font-semibold text-neutral-subtle-bold"
              >
                {{ loadErrorTitleMessage }}
              </h3>
              <p
                v-if="unauthorizedTfxUser"
                class="text-left font-normal text-neutral-subtle-bold"
              >
                Not sure why? Learn more about potential reasons
                <a
                  href="https://help.highway.com/en/articles/12044805-why-can-t-i-see-loads"
                  target="_blank"
                  class="underline"
                >
                  here
                </a>
              </p>
              <p
                v-else
                class="text-left font-normal text-neutral-subtle-bold"
              >
                {{ loadErrorMessage }}
              </p>
            </div>
          </div>

          <div
            v-else-if="loads?.length === 0"
            class="flex w-full flex-col items-center justify-center gap-3 rounded-lg border border-steel-300 bg-white"
            :class="{
              'h-[calc(100vh-166px)]': !bannerError,
              'h-[calc(100vh-194px)]': bannerError,
            }"
          >
            <img
              class="h-20 w-20"
              src="@/assets/magnify.svg"
              alt="magnify icon"
            />

            <div class="mx-20 flex flex-col gap-3">
              <h3 class="text-center text-lg text-gray-400">
                No results found.
              </h3>
              <h3 class="text-center text-lg text-gray-400">
                We're just getting started and new loads are being posted every
                day as more brokers join the Trusted Freight Exchange. Try
                adjusting your search filters to see more options.
              </h3>
            </div>
          </div>

          <div
            v-else
            class="h-min w-full rounded-md border border-steel-300 shadow-md"
          >
            <div
              class="grid grid-cols-22 rounded-t-md border-transparent bg-steel-100 py-3 pl-2 pr-5 text-left font-semibold"
            >
              <p class="col-span-3 text-xs">Broker</p>
              <p class="col-span-3 text-xs">Origin</p>
              <p class="col-span-3 text-xs">Destination</p>
              <p class="col-span-2 text-xs">Trip</p>
              <p class="col-span-3 text-xs">Equipment</p>
              <p class="col-span-2 pl-1 text-xs">Weight</p>
              <p class="col-span-2 text-xs">Type</p>
              <p class="col-span-4 pl-4 text-center text-xs">Rate</p>
            </div>

            <div
              class="overflow-scroll"
              :class="{
                'max-h-[calc(100vh-270px)] min-h-[calc(100vh-270px)]':
                  !bannerError,
                'max-h-[calc(100vh-300px)] min-h-[calc(100vh-300px)]':
                  bannerError,
              }"
            >
              <router-link
                v-for="(load, loadIndex) in loads"
                :key="load.id"
                :to="{
                  name: routerLinkName,
                  params: { id: load.id },
                }"
                class="grid grid-cols-22 border-b-2 border-steel-300 bg-white pl-2 pr-5 font-normal text-neutral-subtle hover:bg-gray-50"
                :class="{
                  'border-t-2': loadIndex === 0,
                }"
                @mouseenter="handleMouseOver(load)"
                @click="handleSelectLoad(load)"
              >
                <div
                  class="col-span-3 flex flex-col items-start justify-center gap-1 py-3"
                >
                  <BrokerImage
                    :image-src="load?.broker_logo?.url"
                    :broker-name="load?.broker_name"
                  ></BrokerImage>

                  <p
                    v-if="load.last_posted_at"
                    class="text-left text-1xs text-neutral-subtle"
                  >
                    Posted {{ fromNowFirstUnit(load.last_posted_at) }} ago
                  </p>
                </div>

                <div
                  class="col-span-3 flex flex-col justify-center py-3 text-start"
                >
                  <p class="min-w-fit text-xs">
                    {{ loadOrigin(load) }}
                  </p>

                  <p class="text-xs font-normal text-steel-700">
                    {{
                      formatStopDate(
                        load?.pickup_time,
                        load?.pickup_time_timezone,
                        load?.pickup_has_time
                      )
                    }}
                  </p>
                </div>

                <div
                  class="col-span-3 flex flex-col justify-center py-3 text-start"
                >
                  <p class="min-w-fit text-xs">
                    {{ loadDestination(load) }}
                  </p>

                  <p class="text-xs text-steel-700">
                    {{
                      formatStopDate(
                        load?.dropoff_time,
                        load?.dropoff_time_timezone,
                        load?.dropoff_has_time
                      )
                    }}
                  </p>
                </div>

                <div class="col-span-2 flex items-center justify-start py-3">
                  <p
                    v-if="load.posted_miles"
                    class="text-xs"
                  >
                    {{
                      (Math.round(load.posted_miles * 10) / 10).toLocaleString()
                    }}
                    mi
                  </p>
                  <p
                    v-else
                    class="text-xs"
                  >
                    -
                  </p>
                </div>

                <div
                  class="col-span-3 flex items-center justify-start py-3 pr-1"
                >
                  <p class="text-left text-xs">
                    {{
                      load.posted_equipment_types?.length > 0
                        ? formatEquipmentType(load.posted_equipment_types)
                        : '-'
                    }}
                  </p>
                </div>

                <div
                  class="col-span-2 flex items-center justify-start py-3 pl-1"
                >
                  <p class="text-xs">
                    {{ loadWeight(load.weight, load.weight_unit) }}
                  </p>
                </div>

                <div class="col-span-2 flex items-center justify-start py-3">
                  <p
                    v-if="load?.is_hazmat_required"
                    class="rounded-md bg-steel-100 px-2 py-[2px] text-left text-xs"
                  >
                    Hazmat
                  </p>
                  <p
                    v-if="load?.is_teams_required"
                    class="rounded-md bg-steel-100 px-2 py-[2px] text-left text-xs"
                  >
                    Teams
                  </p>
                  <p
                    v-if="load?.is_power_only_required"
                    class="rounded-md bg-steel-100 px-2 py-[2px] text-left text-xs"
                  >
                    Power Only
                  </p>
                </div>

                <div class="col-span-4 flex items-center py-3">
                  <div
                    class="flex w-full items-center gap-2"
                    :class="
                      loadRateText(
                        load?.lane_prediction?.posted_rate_assessment
                      )
                        ? 'justify-between'
                        : 'justify-end'
                    "
                  >
                    <span
                      v-if="
                        loadRateText(
                          load?.lane_prediction?.posted_rate_assessment
                        )
                      "
                      class="ml-auto min-w-max rounded px-[6px] py-[2px] text-xs"
                      :class="
                        loadRateClass(
                          load?.lane_prediction?.posted_rate_assessment
                        )
                      "
                    >
                      {{
                        loadRateText(
                          load?.lane_prediction?.posted_rate_assessment
                        )
                      }}
                    </span>
                    <div class="flex min-w-[64px] flex-col items-end">
                      <p class="text-xl font-semibold">
                        {{ formatPrice(load.price, 0) }}
                      </p>
                      <p
                        v-if="load?.price && loadRate(load?.rate_per_mile)"
                        class="text-xs"
                      >
                        {{ loadRate(load?.rate_per_mile) }}/mi
                      </p>
                    </div>
                  </div>
                </div>
              </router-link>
            </div>

            <section
              v-if="!isLoading"
              class="relative flex min-h-[61px] items-center justify-center rounded-b-md border-t border-steel-300 bg-white py-2"
            >
              <p class="absolute left-3 top-6 text-xs text-steel-700">
                {{ numberOfLoadsOnLoadBoard }}
              </p>
              <Pagination
                v-if="loadsPagination?.totalPages > 1"
                :selected-page="+loadsPagination.currentPage"
                :total-pages="+loadsPagination.totalPages"
                @select-page="onSelectPage"
              />
            </section>
          </div>
        </div>
      </div>

      <div
        v-else-if="isLoading"
        class="mt-6"
      >
        <img
          class="loading m-auto h-12 w-12"
          src="@/assets/loading-v2-yellow.svg"
          alt="loading spinner"
        />
      </div>

      <div v-else-if="doesNotMeetRequirements">
        <div
          v-if="isMissingOriginState"
          class="m-auto flex flex-col items-center gap-3"
        >
          <img
            src="@/assets/loads/semi-truck.svg"
            alt="semi truck"
          />

          <h3
            class="text-center text-xl font-semibold text-neutral-subtle-bold"
          >
            Let's get you on the road
          </h3>
          <p class="text-center font-normal text-neutral-subtle-bold">
            To get started, enter an origin, destination, or broker.
          </p>
        </div>

        <div
          v-else
          class="mx-4 flex flex-col gap-3 rounded-lg border border-steel-300 bg-white p-4 shadow-md"
        >
          <h3 class="text-left text-lg font-semibold text-neutral-subtle-bold">
            {{ loadErrorTitleMessage }}
          </h3>
          <p
            v-if="unauthorizedTfxUser"
            class="text-left font-normal text-neutral-subtle-bold"
          >
            Not sure why? Learn more about potential reasons
            <a
              href="https://help.highway.com/en/articles/12044805-why-can-t-i-see-loads"
              target="_blank"
              class="underline"
            >
              here
            </a>
          </p>
          <p
            v-else
            class="text-left font-normal text-neutral-subtle-bold"
          >
            {{ loadErrorMessage }}
          </p>
        </div>
      </div>

      <div
        v-else-if="loads?.length === 0"
        class="m-4 flex flex-col items-center justify-center rounded-lg bg-white py-4"
      >
        <img
          class="h-16 w-16"
          src="@/assets/magnify.svg"
          alt=""
        />

        <div class="mx-2 flex flex-col gap-3">
          <h3 class="text-center text-lg text-gray-400">No results found.</h3>
          <h3 class="text-center text-lg text-gray-400">
            We're just getting started and new loads are being posted every day
            as more brokers join the Trusted Freight Exchange. Try adjusting
            your search filters to see more options.
          </h3>
        </div>
      </div>

      <div
        v-else
        class="grid h-full grid-cols-4 gap-4 p-4"
      >
        <div
          v-for="load in loads"
          :key="load.id"
          class="col-span-4 px-2 md:col-span-2"
        >
          <LoadDetail :load="load"></LoadDetail>
        </div>

        <InfiniteScroll @load-more="loadMoreDetails"></InfiniteScroll>
      </div>
      <div
        v-if="isLoadsRefetching && !isLoading"
        class="pb-12 pt-6"
      >
        <img
          class="loading m-auto h-12 w-12"
          src="@/assets/loading-v2-yellow.svg"
          alt="loading spinner"
        />
      </div>
    </div>
  </div>

  <LoadSortBottomSheet
    :visible="showSortBottomSheet"
    :is-loads-dashboard="isLoadsDashboard"
    @close="closeSortBottomSheet"
  ></LoadSortBottomSheet>

  <post-capacity-modal
    v-if="showCapacityModal"
    :visible="showCapacityModal"
    :is-load-board-view="true"
    @close="closePostCapacityModal"
    @error="handlePostTruckError"
  ></post-capacity-modal>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import debounce from 'lodash.debounce';
import { ButtonComponent, InfiniteScroll, Pagination } from '@/components';
import {
  BrokerImage,
  LoadDetail,
  LoadFiltersDesktop,
  LoadsSearchAndFilter,
  LoadSortBottomSheet,
  LoadSortMenu,
  PostCapacityModal,
} from '@/components/loads';
import { formatPrice, formatStopDate, formatEquipmentType } from '@/utils';
import { fromNowFirstUnit } from '@/common';

const props = defineProps({
  title: String,
  loads: Array,
  isDesktop: Boolean,
  isLoading: Boolean,
  isLoadsRefetching: Boolean,
});

const emit = defineEmits(['search-loads', 'fetch-more', 'select-page']);

const store = useStore();
const router = useRouter();
const showSortBottomSheet = ref(false);

const isLoadsDashboard = computed(() => props.title === 'Find Loads');

const bannerError = computed(
  () =>
    !store.state.errors.forceHideErrorBanner &&
    store.state.errors.showErrorBanner
);

const unauthorizedTfxUser = computed(
  () => store.state.loads?.search_load_unauthorized_user
);

const loadErrorMessage = computed(
  () =>
    store.state.loads?.search_load_error?.message ||
    store.state.loads?.search_load_error
);

const loadErrorTitleMessage = computed(() =>
  unauthorizedTfxUser?.value
    ? "Sorry. You're not eligible for any routes on this lane."
    : "Let's get you on the road."
);

const doesNotMeetRequirements = computed(
  () => Object.entries(store.state.loads?.search_load_error)?.length > 0
);

const isMissingOriginState = computed(
  () =>
    loadErrorMessage?.value ===
    'Origin, destination, or broker required for load searching'
);

const truck_postings = computed(() => store.state.loads?.truck_postings);

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const filtered_carrier_vehicles_length = computed(() => {
  const postedTruckIds = new Set(
    truck_postings.value?.map((truck) => truck.carrier_vehicle?.id)
  );

  const filteredVehicles = carrier_vehicles.value?.filter(
    (vehicle) => !postedTruckIds.has(vehicle.id)
  );
  return filteredVehicles?.length || 0;
});

const disabledPostCapacity = computed(() => {
  if (filtered_carrier_vehicles_length?.value === 0) {
    return true;
  }

  return doesNotMeetRequirements.value && !isMissingOriginState.value;
});

const shouldRedirect = computed(() => {
  return (
    unauthorizedTfxUser.value &&
    store.state.users?.userLoadBoardAccess?.user?.has_pending_coi_request
  );
});

const reRouteToInsuranceExchange = () => {
  console.log('Unauthorized user and has pending COI. Redirecting..');
  router.push({ name: 'File Your Insurance Exchange' });
};

watch(shouldRedirect, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      reRouteToInsuranceExchange();
    }, 0);
  }
});

const resetActive = ref(false);

const handleRefreshView = async () => {
  console.log('Refresh layout view:');
  resetActive.value = true;

  const existingQuery = router.currentRoute?.value?.query;
  const existingSort = existingQuery?.sort || '';
  const query = JSON.parse(localStorage.getItem('load_search_filters')) || {};

  await store.dispatch('loads/searchLoads', {
    query,
    sort: existingSort,
  });
};

watch(
  () => props?.isLoading,
  (newValue) => {
    if (!newValue) {
      resetActive.value = false;
    }
  }
);

const routerLinkName = computed(() => 'Load Detail');

const loadsPagination = computed(() => store.state.loads?.loadboard_pagination);

const isRecommendedView = computed(
  () => store.state.loads?.is_recommended_view
);

const numberOfLoadsOnLoadBoard = computed(() => {
  if (isRecommendedView.value) {
    return `Showing ${props.loads?.length} of ${loadsPagination.value?.totalRecords} recommended load${props?.loads?.length > 1 ? 's' : ''}`;
  } else if (!props.loads) {
    return '';
  } else if (props.loads?.length) {
    return `Showing ${props.loads?.length} of ${loadsPagination.value?.totalRecords} load${props?.loads?.length > 1 ? 's' : ''} you're eligible for`;
  }

  return '';
});

onMounted(async () => {
  const carrier_id =
    store.state.users?.currentUser?.requested_carrier_id ||
    store.state.users?.currentUser?.company?.carrier_id;

  await Promise.all([
    store.dispatch('loads/getCarrierVehicles', {
      id: carrier_id,
      query:
        '&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc',
    }),
    store.dispatch('loads/getTruckPostings', {
      query: '?q[is_active_eq]=1',
    }),
    store.dispatch('loads/getExpiredTruckPostings', {}),
  ]);
});

const setLoadInfo = (load) => {
  store.commit('loads/setPreLoadResponse', load);
  store.commit('loads/setLoadDetail', {
    ...load,
    stops: load?.stops?.map((stop) => ({
      ...stop,
      latitude_for_map: stop?.lat,
      longitude_for_map: stop?.lon,
    })),
  });
};

const handleMouseOver = debounce(async (load) => {
  setLoadInfo(load);
}, 100);

const handleSelectLoad = (load) => {
  if (load?.id !== store.state.loads?.preloaded_load_response?.id) {
    setLoadInfo(load);
  }
};

const showCapacityModal = ref(false);

const openPostCapacityModal = () => {
  showCapacityModal.value = true;
};
const closePostCapacityModal = (refetch) => {
  showCapacityModal.value = !showCapacityModal.value;

  if (refetch) {
    isRefreshSearch.value = true;

    setTimeout(() => {
      isRefreshSearch.value = false;
    }, 500);
  }
};

const handlePostTruckError = () => {};

const openSortBottomSheet = () => {
  showSortBottomSheet.value = true;
};

const closeSortBottomSheet = () => {
  showSortBottomSheet.value = false;
};

const searchLoadsInput = (term) => {
  emit('search-loads', term);
};

const loadMoreDetails = () => {
  emit('fetch-more');
};

const onSelectPage = (page) => {
  emit('select-page', page);
};

const capitalizeWords = (word) => {
  return word
    .toLowerCase()
    .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
    .replace('Llc', 'LLC');
};

const loadOrigin = (load) => {
  const origin = load?.stops?.[0] || load?.origin;

  const city = capitalizeWords(
    origin?.city || origin?.area_five_zip_city || ''
  );
  const state = origin?.state || origin?.area_five_zip_state || '';

  if (!city) {
    return '-';
  }

  return `${city}${state ? ', ' + state : ''}`;
};

const loadDestination = (load) => {
  const destination =
    load?.stops?.[load?.stops?.length - 1] || load?.destination;

  const city = capitalizeWords(
    destination?.city || destination?.area_five_zip_city || ''
  );
  const state = destination?.state || destination?.area_five_zip_state || '';

  if (!city) {
    return '-';
  }

  return `${city}${state ? ', ' + state : ''}`;
};

const isResettingSearch = ref(false);
const isRefreshSearch = ref(false);

const querySort = computed(() => router.currentRoute?.value?.query?.sort || '');

const sortHash = {
  rpm: 'RPM',
  posting_desc: 'Age',
  price_desc: 'Rate',
  distance_asc: 'Shortest Trip',
  distance_desc: 'Longest Trip',
  deadhead: 'Deadhead',
  pickup: 'Pickup',
};

const postingSort = computed(() =>
  querySort?.value ? sortHash[querySort?.value] : 'Best'
);

const weightUnit = {
  pound: 'lbs',
  kilogram: 'kg',
};

const loadWeight = (weight, weight_unit) => {
  if (weight === 0 || !weight) return '-';
  return `${weight?.toLocaleString('en-US')} ${weightUnit[weight_unit]}`;
};

const loadRateClass = (rate) => {
  switch (rate) {
    case 'Great':
      return 'bg-aqua-700 text-white';
    case 'Good':
      return 'bg-aqua-500 text-white';
    case 'Fair':
      return 'bg-aqua-300 text-steel-1100';
    case 'Below':
      return 'bg-red-100 text-red-600';
    default:
      return '';
  }
};

const loadRateText = (rate) => {
  switch (rate) {
    case 'Great':
      return 'Great Deal';
    case 'Good':
      return 'Good Deal';
    default:
      return null;
  }
};

const loadRate = (rate) => {
  if (!rate) return null;
  return formatPrice(rate);
};
</script>

<style>
.icon-flip {
  transform: scale(-1);
}

.table-max {
  max-width: 100rem;
}
</style>
